/*
 * Profile section — V1.9.1 measured design (handoff README §Screens →
 * 4. Profile; App Shell.dc.html is the measured DOM reference).
 *
 *  - Account card: the REAL signed-in Cognito account; Edit (44px teal
 *    outline — teal is fine for ACTIONS; the reserved-status rule applies to
 *    identity) opens the ACCOUNT EDIT HUB (/profile/edit — the summary-card
 *    layout with per-row pencils, CMT-2026-09-03-026), never a blank stepper.
 *  - MANAGED PROFILES — THREE groupings, NEVER merged (PRODUCT-RULES
 *    §Children + Duncan ruling BOARDROOM-2026-09-02-152856 part C):
 *    my children (`guest: false`), someone else's children (`guest: true`,
 *    signing NOT farmed out), and DEFERRED — sent to their parent to sign
 *    (guest entries whose guestSigningState === 'parent', plus entries
 *    derived from guestparent seat records with no linked ChildRecord).
 *    Records read from the P2 child store (src/screens/orders/assignStore.ts
 *    — the SAME records the assign flow picks from). Row note derives from
 *    the record's own data (`Age N · Gender · profile complete|to finish`) —
 *    never a stored flag. A row opens THAT child's profile flow
 *    (/profile/children/:key/step-1, subject-scoped drafts). Guest entries
 *    in the someone-else's group carry the explicit SIGNING flag for the
 *    remaining states (you sign / not yet determined) — Duncan ruling
 *    BOARDROOM-2026-09-02-150714 item 4, derived live from the seat records
 *    (guestSigningState); the 'parent' state is now STRUCTURAL (its own
 *    titled group), never a flag inside the someone-else's group.
 *  - SERVER-HELD CHILD SEATS FIRST (boardroom 2026-10-01, quality F2): the
 *    managed list derives from the caller's /me/holdings — one entry per
 *    child seat the server says the caller holds (kind='child', and
 *    guardian-claimed seats with a child name), labelled child name + event;
 *    tapping opens the SERVER-BACKED child-details form for that ticket
 *    (/get-ready/child-details/{ticketId}). A fresh browser therefore shows
 *    the real children. Device-local assign-store records render ONLY when
 *    the server does not know their seat (legacy / not yet stamped, or a
 *    seatless child added here) — never as the source of truth.
 *  - + Add a child: inline form (first name, last name, DOB as day /
 *    month-NAME / year selects — never a numeric format, gender). Which
 *    fields are asked / mandatory is EVENT-OWNER configuration (childDob,
 *    childGender — the P2 ChildFieldConfig seam; // TODO WIRE source). A
 *    blocked-reason line sits above the buttons naming the SPECIFIC missing
 *    field, in on-screen field order.
 *  - Account rows: only REAL functionality is wired — Sign out (Cognito),
 *    Password (the in-app Cognito forgot/confirm reset primitives, an inline
 *    panel). Two-step verification / Notifications have no backing service:
 *    they render honestly NON-NAVIGABLE (no chevron, no fake state) — no dead
 *    fake settings.
 *  - NO letter-monogram tiles anywhere (PRODUCT-RULES §Logos).
 */
import { useEffect, useState, useSyncExternalStore } from 'react'
import type { CSSProperties, ComponentType } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useShellChrome } from '../components/Shell'
import { useLocale, type TranslateFn } from '../i18n/useLocale'
import { loadSession, clearSession } from '../auth/session'
import { confirmForgotPassword, forgotPassword, globalSignOut } from '../auth/cognitoAuth'
import { getProfile, type CognitoProfile } from '../auth/cognitoProfile'
import { IconBell, IconKey, IconShield, IconSignOut } from '../lib/icons'
import {
  addChild, getAssignStoreSnapshot, getChildProfile, subscribeAssignStore,
  type ChildRecord, type LocalAssignment,
} from './orders/assignStore'
import {
  DEFAULT_CHILD_FIELD_CONFIG, genderOptions, type ChildFieldConfig,
} from './orders/assignModel'
import { ageFromDob, childProfileComplete, DAYS, EMPTY_DRAFT, MONTHS } from './wizard/wizardModel'
import { deleteMyAccount, fetchMyHoldings, MyApiError, myApiBase, type Holding } from '../data/myApi'
import { useLegacyHistory } from '../data/legacyHistory'
import { LegacyHistorySection } from '../components/LegacyHistory'
import { useTicketCredits } from '../data/ticketCredits'
import { TicketCreditsSection } from '../components/TicketCredits'
import { useReferralCodes } from '../data/referralCodes'
import { ReferralCodesSection } from '../components/ReferralCodes'
import { MyNewslettersSection } from '../components/MyNewsletters'

// ---------------------------------------------------------------------------
// Styles (V1.9.1 tokens; DC inline styles transcribed)
// ---------------------------------------------------------------------------

const kickerStyle: CSSProperties = {
  fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.1em',
  textTransform: 'uppercase', color: 'var(--text-faint)', margin: '0 2px 8px',
}
const cardStyle: CSSProperties = {
  background: 'var(--surface)', border: '1px solid var(--hairline-08)', borderRadius: 15,
}
const labelStyle: CSSProperties = {
  fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 6,
}
const inputStyle: CSSProperties = {
  width: '100%', minHeight: 46, border: '1px solid var(--hairline-14)',
  borderRadius: 11, padding: '0 13px', fontSize: 15, background: 'var(--bg-frame)',
  color: 'var(--text)', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
}
const selectStyle: CSSProperties = {
  ...inputStyle, padding: '0 8px', appearance: 'none', WebkitAppearance: 'none',
}
const pillStyle = (selected: boolean): CSSProperties => ({
  borderRadius: 20, padding: '0 15px', minHeight: 44, fontFamily: 'inherit',
  fontSize: 13, fontWeight: 700, cursor: 'pointer',
  ...(selected
    ? { background: 'var(--accent)', color: 'var(--accent-ink)', border: 0 }
    : { background: 'var(--bg-frame)', color: 'var(--text-softbtn)', border: '1px solid var(--hairline-14)' }),
})
/** List chevron — direction-encoding glyph, swapped in code under RTL. */
const chevronStyle: CSSProperties = { color: 'var(--text-chevron)', flex: 'none' }

// ---------------------------------------------------------------------------
// Managed children — the P2 child store IS the source (two pools by `guest`)
// ---------------------------------------------------------------------------

/** Row note — DERIVED from the child's own record (DC kidRow):
 *  `Age N · Gender · profile complete|to finish`. Parts with nothing to say
 *  are simply absent. The guest pool's signing situation is NOT part of this
 *  line any more — it is the dedicated 3-state signing flag below (Duncan
 *  ruling BOARDROOM-2026-09-02-150714 item 4), one pattern, one vocabulary. */
export function childRowNote(c: ChildRecord, t: TranslateFn): string {
  const draft = getChildProfile(c.key) ?? {
    ...EMPTY_DRAFT,
    first: c.first, last: c.last, dobD: c.dobD, dobM: c.dobM, dobY: c.dobY, gender: c.gender,
  }
  const age = ageFromDob(draft)
  const done = childProfileComplete(draft)
  return [
    age !== null ? t('profile.child.age', { n: age }) : null,
    draft.gender || null,
    done ? t('profile.child.complete') : t('profile.child.toFinish'),
  ].filter(Boolean).join(' · ')
}

// ---------------------------------------------------------------------------
// SERVER-held child seats (boardroom 2026-10-01, quality F2)
// ---------------------------------------------------------------------------

/** The caller's CHILD SEATS per the server holdings: kind='child' rows, and
 *  guardian-claimed rows carrying a child name. One entry per seat (V1: the
 *  child identity is per ticket — no cross-ticket merge). */
export function serverChildSeats(holdings: Holding[]): Holding[] {
  return holdings.filter((h) =>
    h.kind === 'child' || (h.claimKind === 'guardian' && (h.holderName ?? '').trim() !== ''),
  )
}

/** true ⇔ a device-local child record is SUPERSEDED by the server: at least
 *  one of its locally-stamped seats is a seat the server holdings know, and
 *  none of them is unknown. Records with no stamped seat (added here, or a
 *  pre-stamp legacy assignment) are never superseded — the server cannot
 *  speak for them. */
export function localChildKnownToServer(
  c: ChildRecord, assigned: Record<string, LocalAssignment>, serverTicketIds: Set<string>,
): boolean {
  const seats = Object.values(assigned).filter((a) => a.childKey === c.key && !!a.ticketId)
  return seats.length > 0 && seats.every((a) => serverTicketIds.has(a.ticketId as string))
}

// ---------------------------------------------------------------------------
// Signing-status flag — guest pool ONLY (Duncan ruling BOARDROOM-2026-09-02-
// 150714 item 4, ratified). Exactly three explicit states on every
// "someone else's children" entry; own children NEVER carry it (their legal
// basis is direct parenthood, not a signing arrangement).
// ---------------------------------------------------------------------------

/** The three ratified states. 'you' = the purchaser holds signing duty
 *  (a live guestchild seat record); 'parent' = the duty is farmed to the
 *  child's own parent (a live guestparent seat record, no guestchild);
 *  'undetermined' = no seat record resolves the duty yet. */
export type GuestSigningState = 'you' | 'parent' | 'undetermined'

/** DERIVE the signing state from the live local seat records — never a
 *  stored flag (PROCESS-3: presented-is-not-configured). Records match by
 *  `childKey`; own-child modes ('child' / 'teen') never contribute. Mixed
 *  guestchild + guestparent across seats resolves to 'you' (the purchaser
 *  holds a live signing duty regardless of what other seats farmed out) —
 *  NOTE: today's writers cannot present that mix (guestparent records carry
 *  no childKey — AssignFlow persistLocalOutcome seam), so no per-seat nuance
 *  line is invented for it. */
export function guestSigningState(
  c: ChildRecord, assigned: Record<string, LocalAssignment>,
): GuestSigningState {
  let parentSigns = false
  for (const a of Object.values(assigned)) {
    if (a.childKey !== c.key) continue
    if (a.mode === 'guestchild') return 'you'
    if (a.mode === 'guestparent') parentSigns = true
  }
  return parentSigns ? 'parent' : 'undetermined'
}

/** Resource key per state — ONE vocabulary for the whole page. Since Duncan
 *  ruling BOARDROOM-2026-09-02-152856 part C the 'parent' state is STRUCTURAL
 *  (the deferred group's title carries it) and its flag never renders on-page;
 *  the key stays — it remains the ratified vocabulary and the map stays total
 *  over GuestSigningState. */
export const SIGNING_STATE_KEY = {
  you: 'profile.signing.you',
  parent: 'profile.signing.parent',
  undetermined: 'profile.signing.undetermined',
} as const satisfies Record<GuestSigningState, string>

/** The flag line itself — matches the entry sub-line typography (11px under
 *  the 14px name), RTL-safe (plain translated text, logical text-align via
 *  the row's `textAlign: 'start'`, no direction-encoding glyphs). */
function SigningFlag({ state, t }: { state: GuestSigningState; t: TranslateFn }) {
  return (
    <span
      data-testid="profile-signing-flag"
      data-state={state}
      style={{
        display: 'block', fontSize: 11, fontWeight: 700, marginTop: 1,
        color: state === 'undetermined' ? 'var(--text-faint)' : 'var(--text-secondary)',
      }}
    >
      {t('profile.signing.label')} {t(SIGNING_STATE_KEY[state])}
    </span>
  )
}

// ---------------------------------------------------------------------------
// DEFERRED grouping — "sent to their parent to sign" (Duncan ruling
// BOARDROOM-2026-09-02-152856 part C, structural). Two sources feed it:
//  1. guest ChildRecords whose guestSigningState === 'parent' (a linked
//     guestparent seat record) — they MOVE here from the someone-else's group
//     (never double-rendered) and keep their profile-flow affordance;
//  2. guestparent seat records with NO linked guest ChildRecord — today's
//     writers (AssignFlow persistLocalOutcome) stamp guestparent records with
//     childKey:'' and create no ChildRecord, so THESE are what light the
//     group under current data. They carry the child's typed name +
//     parentName/parentEmail; display-only (no managed profile behind them —
//     no edit affordance, honestly).
// ---------------------------------------------------------------------------

/** One assignment-derived deferred entry (source 2 above). */
export interface DeferredAssignmentEntry {
  /** Stable render key — the seat key of the first record for this entry. */
  key: string
  name: string
  parentName: string
  parentEmail: string
}

/** DERIVE the assignment-only deferred entries — guestparent seat records
 *  whose childKey resolves to NO guest ChildRecord (linked records render via
 *  their ChildRecord in the deferred group instead — never both). Multiple
 *  seats farmed to the same parent for the same child collapse to ONE entry
 *  (the group lists children, not seats). */
export function deferredAssignmentEntries(
  assigned: Record<string, LocalAssignment>, children: ChildRecord[],
): DeferredAssignmentEntry[] {
  const guestKeys = new Set(children.filter((c) => c.guest).map((c) => c.key))
  const seen = new Set<string>()
  const out: DeferredAssignmentEntry[] = []
  for (const a of Object.values(assigned)) {
    if (a.mode !== 'guestparent') continue
    if (a.childKey && guestKeys.has(a.childKey)) continue // linked → ChildRecord renders it
    const dedupe = `${a.name.trim().toLowerCase()}|${a.parentEmail.trim().toLowerCase()}`
    if (seen.has(dedupe)) continue
    seen.add(dedupe)
    out.push({ key: a.seatKey, name: a.name, parentName: a.parentName, parentEmail: a.parentEmail })
  }
  return out
}

/** The deferred entry's sub-line: `sent to {parent}` — parent name when
 *  typed at assign, else the parent's email, else an honest generic (never a
 *  fabricated name, PROCESS-3). */
export function deferredSentToLine(parentName: string, parentEmail: string, t: TranslateFn): string {
  const to = parentName.trim() || parentEmail.trim()
  return to ? t('profile.deferred.sentTo', { parentName: to }) : t('profile.deferred.sentToParent')
}

// ---------------------------------------------------------------------------
// Sign out — clear the local session + best-effort Cognito GlobalSignOut, then
// return to the branded in-app login. NO amazoncognito.com hop on logout
// (email-first auth has no hosted UI).
// ---------------------------------------------------------------------------

function signOut() {
  const session = loadSession()
  clearSession()
  // Best-effort revoke of the refresh/access tokens server-side; ignore failure
  // (the local session is already gone, which is what gates the app).
  if (session?.accessToken) void globalSignOut(session.accessToken).catch(() => {})
  window.location.assign('/')
}

// ---------------------------------------------------------------------------
// Delete-account panel (LOCKED PROGRAMME-RULING-2026-10-02-ERASURE-ANONYMISE-
// ACCOUNT-KEEP-TRANSACTIONS-AND-TWO-REQUEST-TYPES-V1 v2; WU
// PROJ-0062-WU-ERASURE-01). The separate "delete your Trumin account" control
// the help-desk reply links to. The server anonymises the account record only;
// the copy says plainly what is kept (orders/payments/tickets 7 years for legal
// use; published results stay public). Typing DELETE guards against a slip.
// ---------------------------------------------------------------------------

function DeleteAccountPanel({ t, onClose }: { t: TranslateFn; onClose: () => void }) {
  const [typed, setTyped] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const armed = typed.trim().toUpperCase() === 'DELETE'

  const run = async () => {
    if (busy || !armed) return
    const session = loadSession()
    const base = myApiBase()
    if (!session || !base) {
      setError(t('profile.delete.unavailable'))
      return
    }
    setBusy(true)
    setError(null)
    try {
      await deleteMyAccount(session.idToken, base)
      // The account is disabled server-side; drop the local session and leave.
      clearSession()
      window.location.assign('/')
    } catch (e) {
      const code = e instanceof MyApiError ? e.code : ''
      setError(
        code === 'deletion_unavailable'
          ? t('profile.delete.unavailable')
          : code === 'deletion_incomplete'
            ? t('profile.delete.incomplete')
            : t('profile.delete.failed'),
      )
      setBusy(false)
    }
  }

  return (
    <div data-testid="profile-delete-panel" style={{ padding: '4px 15px 15px' }}>
      <div style={{ fontSize: 12.5, color: 'var(--text)', lineHeight: 1.5, marginBottom: 8, fontWeight: 600 }}>{t('profile.delete.intro')}</div>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 12 }}>{t('profile.delete.kept')}</div>
      <label style={labelStyle}>{t('profile.delete.typeToConfirm')}</label>
      <input data-testid="profile-delete-typed" value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" style={inputStyle} />
      {error && (
        <div role="alert" style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--error)', marginTop: 10 }}>{error}</div>
      )}
      <div style={{ display: 'flex', gap: 9, marginTop: 12 }}>
        <button
          data-testid="profile-delete-cancel"
          onClick={onClose}
          style={{ flex: 'none', border: '1px solid var(--hairline-16)', borderRadius: 11, minHeight: 44, padding: '0 14px', background: 'transparent', color: 'var(--text)', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
        >{t('profile.delete.cancel')}</button>
        <button
          data-testid="profile-delete-confirm"
          disabled={!armed || busy}
          onClick={() => { void run() }}
          style={{ flex: 1, border: 0, borderRadius: 11, minHeight: 44, background: armed ? 'var(--error)' : 'var(--hairline-16)', color: '#fff', fontFamily: 'inherit', fontSize: 13, fontWeight: 800, cursor: armed && !busy ? 'pointer' : 'not-allowed' }}
        >{busy ? t('profile.delete.deleting') : t('profile.delete.confirm')}</button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Password panel — REAL Cognito forgot/confirm primitives (the in-app reset
// flow), inline. The only settings destination with real backing besides
// Sign out.
// ---------------------------------------------------------------------------

function PasswordPanel({ email, username, t, onClose }: { email: string; username: string; t: TranslateFn; onClose: () => void }) {
  const [stage, setStage] = useState<'intro' | 'code' | 'done'>('intro')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [pw, setPw] = useState('')

  // The reset MUST target the authenticated account's EXACT Cognito username
  // (the id-token `cognito:username`), not the display email: the pool is
  // case-sensitive and PreventUserExistenceErrors turns ForgotPassword on a
  // lowercased-away legacy username into a SIMULATED success — a silent
  // no-op for every signed-in mixed-case legacy user (PR#128 review P2-1).
  const sendCode = async () => {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      await forgotPassword(username, { exactUsername: true })
      setStage('code')
    } catch (e) {
      setError(e instanceof Error ? e.message : t('profile.password.sendFailed'))
    } finally {
      setBusy(false)
    }
  }
  const confirm = async () => {
    if (busy || !code.trim() || !pw) return
    setBusy(true)
    setError(null)
    try {
      await confirmForgotPassword(username, code.trim(), pw, { exactUsername: true })
      setStage('done')
    } catch (e) {
      // Surface Cognito's own message (incl. its password-policy text).
      setError(e instanceof Error ? e.message : t('profile.password.changeFailed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div data-testid="profile-password-panel" style={{ padding: '4px 15px 15px' }}>
      {stage === 'done' ? (
        <div role="status" style={{ fontSize: 12.5, color: 'var(--success)', fontWeight: 700, lineHeight: 1.5 }}>
          {t('profile.password.done')}
        </div>
      ) : (
        <>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 10 }}>
            {t('profile.password.intro', { email })}
          </div>
          {stage === 'code' && (
            <>
              <label style={labelStyle}>{t('profile.password.code')}</label>
              <input data-testid="profile-password-code" value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" style={inputStyle} />
              <div style={{ height: 12 }} />
              <label style={labelStyle}>{t('profile.password.new')}</label>
              <input data-testid="profile-password-new" type="password" value={pw} onChange={(e) => setPw(e.target.value)} style={inputStyle} />
            </>
          )}
          {error && (
            <div role="alert" style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--error)', marginTop: 10 }}>{error}</div>
          )}
          <div style={{ display: 'flex', gap: 9, marginTop: 12 }}>
            <button
              data-testid="profile-password-close"
              onClick={onClose}
              style={{ flex: 'none', border: '1px solid var(--hairline-16)', borderRadius: 11, minHeight: 44, padding: '0 14px', background: 'transparent', color: 'var(--text)', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
            >{t('profile.password.close')}</button>
            {stage === 'intro' ? (
              <button
                data-testid="profile-password-send"
                onClick={() => { void sendCode() }}
                style={{ flex: 1, border: 0, borderRadius: 11, minHeight: 44, background: 'var(--accent)', color: 'var(--accent-ink)', fontFamily: 'inherit', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}
              >{busy ? t('profile.password.sending') : t('profile.password.send')}</button>
            ) : (
              <button
                data-testid="profile-password-confirm"
                onClick={() => { void confirm() }}
                aria-disabled={!code.trim() || !pw}
                style={{ flex: 1, border: 0, borderRadius: 11, minHeight: 44, background: 'var(--accent)', color: 'var(--accent-ink)', fontFamily: 'inherit', fontSize: 13, fontWeight: 800, ...(code.trim() && pw ? { cursor: 'pointer' } : { opacity: 0.35, cursor: 'not-allowed' }) }}
              >{busy ? t('profile.password.changing') : t('profile.password.confirm')}</button>
            )}
          </div>
        </>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Add-a-child inline form (README §Screens 4: first name, last name, DOB as
// day / month-NAME / year selects — never numeric — and gender; presence /
// requiredness is event-owner configuration via the P2 ChildFieldConfig seam)
// ---------------------------------------------------------------------------

interface KidForm {
  first: string
  last: string
  dobD: string
  dobM: string
  dobY: string
  gender: string
}

const EMPTY_KID: KidForm = { first: '', last: '', dobD: '', dobM: '', dobY: '', gender: '' }

/** Child year range for the add form (a child's birth year can be recent). */
const KID_YEARS = Array.from({ length: 25 }, (_, i) => String(new Date().getFullYear() - i))

/** Blocked reason, in on-screen field order: names → DOB → gender (QA §9.3).
 *  Reuses the assign flow's phrase keys — one voice for one form. */
export function addChildBlockReason(k: KidForm, cfg: ChildFieldConfig, t: TranslateFn): string | null {
  if (!k.first.trim() || !k.last.trim()) return t('assign.block.childNames')
  if (cfg.childDob === 'Required' && !(k.dobD && k.dobM && k.dobY)) return t('assign.block.childDob')
  if (cfg.childGender === 'Required' && !k.gender) return t('assign.block.childGender')
  return null
}

function AddChildForm({ config, t, onCancel, onSaved }: {
  config: ChildFieldConfig
  t: TranslateFn
  onCancel: () => void
  onSaved: () => void
}) {
  const [kid, setKid] = useState<KidForm>(EMPTY_KID)
  const set = <K extends keyof KidForm>(key: K, v: KidForm[K]) => setKid((s) => ({ ...s, [key]: v }))
  const reason = addChildBlockReason(kid, config, t)
  const ok = reason === null
  const askGender = config.childGender !== 'Not asked'
  const dobLabel = config.childDob === 'Required'
    ? t('profile.addChild.dob')
    : `${t('profile.addChild.dob')} ${t('assign.field.optionalSuffix')}`
  const genderLabel = config.childGender === 'Required'
    ? t('profile.addChild.gender')
    : `${t('profile.addChild.gender')} ${t('assign.field.optionalSuffix')}`

  const save = () => {
    if (!ok) return
    // Own-children pool (`guest: false`) — the guest pool is only ever entered
    // through the assign flow's permission route, never from Profile.
    addChild({
      guest: false,
      first: kid.first.trim(), last: kid.last.trim(),
      dobD: kid.dobD, dobM: kid.dobM, dobY: kid.dobY,
      gender: kid.gender,
    })
    onSaved()
  }

  return (
    <div data-testid="profile-addchild-panel" style={{ background: 'var(--surface)', border: '1px solid var(--hairline-12)', borderRadius: 15, padding: '15px 16px', marginBottom: 22 }}>
      <div style={{ fontSize: 13.5, fontWeight: 800, marginBottom: 4 }}>{t('profile.addChild.title')}</div>
      <div style={{ fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: 14 }}>
        {t('profile.addChild.sub')}
      </div>
      <label style={labelStyle}>{t('profile.addChild.first')}</label>
      <input data-testid="profile-kid-first" value={kid.first} onChange={(e) => set('first', e.target.value)} style={inputStyle} />
      <div style={{ height: 12 }} />
      <label style={labelStyle}>{t('profile.addChild.last')}</label>
      <input data-testid="profile-kid-last" value={kid.last} onChange={(e) => set('last', e.target.value)} style={inputStyle} />
      <div style={{ height: 14 }} />
      {/* DOB: day / month-NAME / year selects — never a numeric format.
          Placeholder sentinels + aria-labels through the resource (the assign
          flow's assign.field.dob.* keys — one voice for one form). */}
      <label style={labelStyle}>{dobLabel}</label>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr 1fr', gap: 8 }}>
        <select data-testid="profile-kid-dob-day" aria-label={t('assign.field.dob.day')} value={kid.dobD || t('assign.field.dob.day')} onChange={(e) => set('dobD', e.target.value === t('assign.field.dob.day') ? '' : e.target.value)} style={selectStyle}>
          {[t('assign.field.dob.day'), ...DAYS].map((d) => <option key={d}>{d}</option>)}
        </select>
        <select data-testid="profile-kid-dob-month" aria-label={t('assign.field.dob.month')} value={kid.dobM || t('assign.field.dob.month')} onChange={(e) => set('dobM', e.target.value === t('assign.field.dob.month') ? '' : e.target.value)} style={selectStyle}>
          {[t('assign.field.dob.month'), ...MONTHS].map((m) => <option key={m}>{m}</option>)}
        </select>
        <select data-testid="profile-kid-dob-year" aria-label={t('assign.field.dob.year')} value={kid.dobY || t('assign.field.dob.year')} onChange={(e) => set('dobY', e.target.value === t('assign.field.dob.year') ? '' : e.target.value)} style={selectStyle}>
          {[t('assign.field.dob.year'), ...KID_YEARS].map((y) => <option key={y}>{y}</option>)}
        </select>
      </div>
      {/* Gender renders ONLY when the owner asks (childGender ≠ Not asked);
          options follow the event's gender policy. */}
      {askGender && (
        <>
          <div style={{ height: 14 }} />
          <label style={labelStyle}>{genderLabel}</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {genderOptions(config.genderPolicy).map((g) => (
              <button key={g} data-testid="profile-kid-gender" data-value={g} onClick={() => set('gender', kid.gender === g ? '' : g)} style={pillStyle(kid.gender === g)}>{g}</button>
            ))}
          </div>
        </>
      )}
      {/* Blocked-reason line above the buttons — the SPECIFIC missing field. */}
      {!ok && (
        <div data-testid="profile-kid-block" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 15, padding: '10px 12px', borderRadius: 11, background: 'rgba(230,193,90,.1)', border: '1px solid rgba(230,193,90,.28)' }}>
          <span style={{ flex: 'none', color: 'var(--warn)', fontSize: 13 }}>⚠</span>
          <span style={{ fontSize: 11.5, lineHeight: 1.45, color: 'var(--warn)', fontWeight: 700 }}>{reason}</span>
        </div>
      )}
      <div style={{ display: 'flex', gap: 9, marginTop: 15 }}>
        <button
          data-testid="profile-kid-cancel"
          onClick={onCancel}
          style={{ flex: 'none', border: '1px solid var(--hairline-16)', borderRadius: 11, minHeight: 44, padding: '0 14px', background: 'transparent', color: 'var(--text)', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
        >{t('profile.addChild.cancel')}</button>
        <button
          data-testid="profile-kid-save"
          onClick={save}
          aria-disabled={!ok}
          style={{ flex: 1, border: 0, borderRadius: 11, minHeight: 44, background: 'var(--accent)', color: 'var(--accent-ink)', fontFamily: 'inherit', fontSize: 13, fontWeight: 800, ...(ok ? { cursor: 'pointer' } : { opacity: 0.35, cursor: 'not-allowed' }) }}
        >{t('profile.addChild.save')}</button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function Profile({ childConfig = DEFAULT_CHILD_FIELD_CONFIG }: {
  /** Event-owner add-a-child field configuration (P2 seam — // TODO WIRE). */
  childConfig?: ChildFieldConfig
}) {
  const { showPageHead } = useShellChrome()
  const { t, dir } = useLocale()
  const navigate = useNavigate()
  const location = useLocation()
  // Row affordance chevron encodes direction — swapped in code (README §RTL).
  const rowChev = dir === 'rtl' ? '‹' : '›'

  // --- account card: real Cognito account, honest states while loading ---
  const [p, setP] = useState<CognitoProfile | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  useEffect(() => {
    const session = loadSession()
    if (!session) return
    let cancelled = false
    getProfile(session.accessToken)
      .then((profile) => { if (!cancelled) setP(profile) })
      .catch((e) => { if (!cancelled) setLoadError(e instanceof Error ? e.message : t('profile.loadFailed')) })
    return () => { cancelled = true }
  }, [])

  const session = loadSession()
  const name = [p?.givenName, p?.familyName].filter(Boolean).join(' ')
  const email = p?.email ?? session?.claims.email ?? ''
  // Exact Cognito username of the SIGNED-IN account, for credentialed calls
  // (change-password). Falls back to the display email only if the claim is
  // somehow absent — Cognito mints `cognito:username` on every ID token.
  const cognitoUsernameClaim = session?.claims['cognito:username']
  const accountUsername = typeof cognitoUsernameClaim === 'string' && cognitoUsernameClaim.trim() ? cognitoUsernameClaim.trim() : email

  // --- managed children: THREE groupings from the P2 store, never merged
  //     (BOARDROOM-2026-09-02-152856 part C). Guest records whose signing
  //     duty is farmed to the child's own parent MOVE to the deferred group —
  //     they never double-render in the someone-else's group. ---
  const store = useSyncExternalStore(subscribeAssignStore, getAssignStoreSnapshot, getAssignStoreSnapshot)
  // SERVER child seats (F2): the source of truth for the managed list. null
  // = still loading (no premature "No one yet" on a fresh browser); [] when
  // the read failed or my-api is not configured (the local list stands).
  const [serverHoldings, setServerHoldings] = useState<Holding[] | null>(() =>
    loadSession() && myApiBase() ? null : [],
  )
  useEffect(() => {
    const s = loadSession()
    const base = myApiBase()
    if (!s || !base) return
    let cancelled = false
    fetchMyHoldings(s.idToken, base)
      .then((h) => { if (!cancelled) setServerHoldings(h) })
      .catch(() => { if (!cancelled) setServerHoldings([]) })
    return () => { cancelled = true }
  }, [])
  const serverSeats = serverChildSeats(serverHoldings ?? [])
  // Legacy customer history (PROJ-0062-WU-LEGACY-HISTORY-01) — flag-gated,
  // null on every non-visible / failed read (renders nothing).
  const legacyHistory = useLegacyHistory()
  // Account Ticket Credit (Apex postponement retention credit) — flag-gated
  // (VITE_TICKET_CREDITS_ENABLED), null on flag off / 404 / failed read.
  const ticketCredits = useTicketCredits()
  const referralCodes = useReferralCodes()
  const serverTicketIds = new Set((serverHoldings ?? []).map((h) => h.ticketId))
  // Local records only where the server does not know the seat (legacy).
  const localChildren = store.children.filter((c) => !localChildKnownToServer(c, store.assigned, serverTicketIds))
  const myKids = localChildren.filter((c) => !c.guest)
  const guestKidsAll = localChildren.filter((c) => c.guest)
  const guestKids = guestKidsAll.filter((c) => guestSigningState(c, store.assigned) !== 'parent')
  const deferredKids = guestKidsAll.filter((c) => guestSigningState(c, store.assigned) === 'parent')
  const deferredDerived = deferredAssignmentEntries(store.assigned, store.children)

  // Inline panel; the optional route /profile/children/new resolves here via
  // App.tsx and opens the panel.
  const [adding, setAdding] = useState(location.pathname === '/profile/children/new')
  const [passwordOpen, setPasswordOpen] = useState(false)
  // Deep link for the help-desk "also delete your Trumin account" reply
  // (settings #695): /profile?action=delete-account opens the panel directly.
  const [deleteOpen, setDeleteOpen] = useState(
    () => typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('action') === 'delete-account',
  )

  const openChildFlow = (c: ChildRecord) => navigate(`/profile/children/${c.key}/step-1`)

  /** A SERVER child seat → its server-backed child-details form (by ticket). */
  const serverSeatRow = (h: Holding) => (
    <button
      key={h.ticketId}
      data-testid="profile-child-seat-row"
      data-ticket={h.ticketId}
      onClick={() => navigate(`/get-ready/child-details/${encodeURIComponent(h.ticketId)}`, {
        state: { eventName: h.eventName, eventId: h.eventId, returnTo: '/profile' },
      })}
      style={{ display: 'flex', alignItems: 'center', gap: 11, width: '100%', border: 0, background: 'transparent', color: 'var(--text)', fontFamily: 'inherit', textAlign: 'start', padding: '12px 10px', cursor: 'pointer', minHeight: 44 }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 14, fontWeight: 700 }}>
          {(h.holderName ?? '').trim() || t('profile.child.fallbackName')}
        </span>
        <span data-testid="profile-child-seat-note" style={{ display: 'block', fontSize: 11, color: 'var(--text-secondary)' }}>
          {[t('profile.child.seat', { event: h.eventName }), (h.ticketTypeName ?? '').trim() || null].filter(Boolean).join(' · ')}
        </span>
      </span>
      <span style={chevronStyle} aria-hidden>{rowChev}</span>
    </button>
  )

  /** Sub-line for a LINKED deferred entry — read off the child's own
   *  guestparent seat record (the record that farmed the duty out). */
  const sentToForKid = (c: ChildRecord): string => {
    const a = Object.values(store.assigned)
      .find((x) => x.mode === 'guestparent' && x.childKey === c.key)
    return deferredSentToLine(a?.parentName ?? '', a?.parentEmail ?? '', t)
  }

  const childRow = (c: ChildRecord, deferredSentTo?: string) => {
    const note = childRowNote(c, t)
    // Signing flag — guest pool ONLY (own children never carry one; their
    // legal basis is parenthood, not a signing arrangement). Derived live
    // from the seat records, never a stored flag. In the DEFERRED group the
    // group title IS the state (structural, BOARDROOM-2026-09-02-152856
    // part C) — the row carries the sent-to line instead, never a redundant
    // 'parent' flag.
    const signing = c.guest && deferredSentTo === undefined
      ? guestSigningState(c, store.assigned)
      : null
    return (
      <button
        key={c.key}
        data-testid="profile-child-row"
        data-child={c.key}
        data-guest={c.guest ? 'true' : 'false'}
        onClick={() => openChildFlow(c)}
        style={{ display: 'flex', alignItems: 'center', gap: 11, width: '100%', border: 0, background: 'transparent', color: 'var(--text)', fontFamily: 'inherit', textAlign: 'start', padding: '12px 10px', cursor: 'pointer', minHeight: 44 }}
      >
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 14, fontWeight: 700 }}>
            {`${c.first} ${c.last}`.trim() || t('profile.child.fallbackName')}
          </span>
          {/* Nothing renders when there is nothing worth saying. */}
          {note && (
            <span data-testid="profile-child-note" style={{ display: 'block', fontSize: 11, color: 'var(--text-secondary)' }}>{note}</span>
          )}
          {/* EVERY someone-else's entry carries the explicit signing flag —
              including the "(not yet determined)" state (the ruling wants the
              unresolved case said out loud, not implied by absence). */}
          {signing && <SigningFlag state={signing} t={t} />}
          {deferredSentTo !== undefined && (
            <span data-testid="profile-deferred-sentto" style={{ display: 'block', fontSize: 11, fontWeight: 700, marginTop: 1, color: 'var(--text-secondary)' }}>{deferredSentTo}</span>
          )}
        </span>
        <span style={chevronStyle} aria-hidden>{rowChev}</span>
      </button>
    )
  }

  /** Assignment-derived deferred entry — NO managed profile behind it, so it
   *  is display-only: a plain div, no onClick, no chevron, no fake edit
   *  affordance (honest — there is nothing to open). */
  const deferredDerivedRow = (e: DeferredAssignmentEntry) => (
    <div
      key={e.key}
      data-testid="profile-deferred-entry"
      style={{ display: 'flex', alignItems: 'center', gap: 11, width: '100%', color: 'var(--text)', textAlign: 'start', padding: '12px 10px', minHeight: 44, boxSizing: 'border-box' }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 14, fontWeight: 700 }}>
          {e.name.trim() || t('profile.child.fallbackName')}
        </span>
        <span data-testid="profile-deferred-sentto" style={{ display: 'block', fontSize: 11, fontWeight: 700, marginTop: 1, color: 'var(--text-secondary)' }}>
          {deferredSentToLine(e.parentName, e.parentEmail, t)}
        </span>
      </span>
    </div>
  )

  // --- account settings rows ---
  interface SettingsRow {
    k: 'password' | '2sv' | 'notifications' | 'signout' | 'delete'
    label: string
    meta: string
    Icon: ComponentType<{ size?: number }>
    /** undefined = HONESTLY non-navigable (no backing service). */
    onClick?: () => void
  }
  const settings: SettingsRow[] = [
    { k: 'password', label: t('profile.settings.password'), meta: t('profile.settings.password.meta'), Icon: IconKey, onClick: () => setPasswordOpen((v) => !v) },
    // No MFA / notification services are wired: no fake `On` state
    // (PROCESS-3), no dead navigation — the row states its honest absence.
    { k: '2sv', label: t('profile.settings.twoStep'), meta: t('profile.settings.notAvailable'), Icon: IconShield },
    { k: 'notifications', label: t('profile.settings.notifications'), meta: t('profile.settings.notAvailable'), Icon: IconBell },
    { k: 'signout', label: t('profile.settings.signOut'), meta: '', Icon: IconSignOut, onClick: signOut },
    { k: 'delete', label: t('profile.settings.deleteAccount'), meta: '', Icon: IconShield, onClick: () => setDeleteOpen((v) => !v) },
  ]

  return (
    <div id="profile.root">
      {/* Desktop h1 (page h1s do not render on mobile) */}
      {showPageHead && (
        <h1 data-testid="profile-head" style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.02em', margin: '0 0 18px' }}>{t('profile.head.title')}</h1>
      )}

      {/* ACCOUNT CARD — name 18/800 + muted email + outline teal Edit (44px) */}
      <div id="profile.account" data-testid="profile-account" style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: 13, padding: 15, marginBottom: 16 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div data-testid="profile-name" style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.01em' }}>
            {/* Honest fallback while attributes are absent/loading — a
                generic label, never a fabricated person name. */}
            {name || t('profile.account.fallbackName')}
          </div>
          <div data-testid="profile-email" style={{ fontSize: 12.5, color: 'var(--text-secondary)', overflowWrap: 'anywhere' }}>{email}</div>
          {loadError && (
            <div role="alert" style={{ fontSize: 11.5, color: 'var(--error)', marginTop: 4 }}>{loadError}</div>
          )}
        </div>
        <button
          data-testid="profile-edit"
          // CMT-2026-09-03-026 (Duncan's blank-stepper defect): Edit opens the
          // ACCOUNT EDIT HUB — the summary-card layout with per-row pencils —
          // NEVER a bare step-1 of the stepper. The hub's pencils deep-link
          // into the wizard pre-filled; an incomplete profile gets the hub's
          // "Add these now" resume into the flow.
          onClick={() => navigate('/profile/edit')}
          style={{ flex: 'none', border: '1px solid rgba(63,224,200,.35)', borderRadius: 9, background: 'transparent', color: 'var(--accent-bright)', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', minHeight: 44, padding: '0 14px' }}
        >{t('profile.account.edit')}</button>
      </div>

      {/* MANAGED PROFILES — pool 1: MY children (`guest: false`) */}
      <div id="profile.managed">
        <div style={kickerStyle}>{t('profile.managed.kicker')}</div>
        {serverSeats.length > 0 || myKids.length > 0 ? (
          <div data-testid="profile-my-children" style={{ ...cardStyle, padding: '5px 6px', marginBottom: 11 }}>
            {serverSeats.map(serverSeatRow)}
            {myKids.map((c) => childRow(c))}
          </div>
        ) : serverHoldings === null ? (
          // Server read in flight — no premature empty state.
          <div data-testid="profile-children-loading" role="status" style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5, padding: '0 2px 11px' }}>
            {t('profile.managed.loading')}
          </div>
        ) : (
          // Honest empty state.
          <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5, padding: '0 2px 11px' }}>
            {t('profile.managed.empty')}
          </div>
        )}
      </div>

      {/* + ADD A CHILD — dashed affordance → inline form. Children never get
          a login: no email/password anywhere on this form. */}
      <div id="profile.addchild">
        {!adding ? (
          <button
            data-testid="profile-add-child"
            onClick={() => setAdding(true)}
            style={{ width: '100%', minHeight: 46, border: '1.5px dashed var(--hairline-16)', borderRadius: 12, background: 'transparent', color: 'var(--text)', fontSize: 13.5, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer', marginBottom: 22 }}
          >{t('profile.addChild.open')}</button>
        ) : (
          <AddChildForm
            config={childConfig}
            t={t}
            onCancel={() => setAdding(false)}
            onSaved={() => setAdding(false)}
          />
        )}
      </div>

      {/* MANAGED PROFILES — group 2: SOMEONE ELSE'S children (`guest: true`,
          signing NOT farmed out — 'you' / 'undetermined' only; 'parent'
          entries live in the deferred group below, never here). A separate
          section, never merged with the first — the legal basis for signing
          differs (PRODUCT-RULES §Children). Absent when empty (this pool is
          only entered through the assign flow). */}
      {guestKids.length > 0 && (
        <div id="profile.guests">
          <div style={{ ...kickerStyle, margin: '6px 2px 8px' }}>{t('profile.managed.guestKicker')}</div>
          <div data-testid="profile-guest-children" style={{ ...cardStyle, padding: '5px 6px', marginBottom: 22 }}>
            {guestKids.map((c) => childRow(c))}
          </div>
        </div>
      )}

      {/* MANAGED PROFILES — group 3: DEFERRED — sent to their parent to sign
          (Duncan ruling BOARDROOM-2026-09-02-152856 part C, structural).
          Placement judgment: LAST of the child groupings, after the someone-
          else's group — the page reads in decreasing order of the account's
          own responsibility (your children → children you still act for →
          children whose signing left your hands), and the two assign-flow-fed
          conditional sections stay adjacent, keeping the established kicker +
          card rhythm. Absent ENTIRELY when empty — no empty-state header
          litter; the structure lights when data arrives. Linked ChildRecords
          keep their profile-flow row; assignment-derived entries (today's
          writers) are display-only. */}
      {(deferredKids.length > 0 || deferredDerived.length > 0) && (
        <div id="profile.deferred">
          <div style={{ ...kickerStyle, margin: '6px 2px 8px' }}>{t('profile.managed.deferredKicker')}</div>
          <div data-testid="profile-deferred-children" style={{ ...cardStyle, padding: '5px 6px', marginBottom: 22 }}>
            {deferredKids.map((c) => childRow(c, sentToForKid(c)))}
            {deferredDerived.map(deferredDerivedRow)}
          </div>
        </div>
      )}

      {/* TICKET CREDIT — account-level retention credit(s) (behind
          VITE_TICKET_CREDITS_ENABLED; absent on 404 / no credits). */}
      <TicketCreditsSection view={ticketCredits} />

      {/* REFERRALS — "Your referral code" per active code (behind
          VITE_REFERRALS_ENABLED; absent on 404 / no codes). /referrals is the
          canonical page; Profile shows the same cards. */}
      <ReferralCodesSection view={referralCodes.view} />

      {/* MY NEWSLETTERS — per-brand marketing switches (behind
          VITE_BRAND_SIGNUP_ENABLED; hidden when my-api's route is off). */}
      <MyNewslettersSection />

      {/* YOUR TRUMIN HISTORY — the earlier-account summary (behind
          VITE_LEGACY_HISTORY_ENABLED; linked accounts only; absent otherwise). */}
      <LegacyHistorySection legacy={legacyHistory} />

      {/* ACCOUNT settings rows — only REAL functionality is navigable. */}
      <div id="profile.settings">
        <div style={kickerStyle}>{t('profile.settings.kicker')}</div>
        <div style={{ ...cardStyle, overflow: 'hidden' }}>
          {settings.map((s) => (
            <div key={s.k}>
              {s.onClick ? (
                <button
                  data-testid="profile-settings-row"
                  data-k={s.k}
                  onClick={s.onClick}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', border: 0, background: 'transparent', color: 'var(--text)', fontFamily: 'inherit', textAlign: 'start', padding: '14px 15px', minHeight: 44, cursor: 'pointer', borderBottom: '1px solid var(--hairline-06)' }}
                >
                  <span aria-hidden style={{ flex: 'none', display: 'inline-flex', color: 'var(--text-secondary)' }}><s.Icon size={16} /></span>
                  <span style={{ flex: 1, fontSize: 13.5, fontWeight: 600 }}>{s.label}</span>
                  {s.meta && <span style={{ fontSize: 12, color: 'var(--text-faint)' }}>{s.meta}</span>}
                  <span style={chevronStyle} aria-hidden>{rowChev}</span>
                </button>
              ) : (
                // Honestly NON-NAVIGABLE: no chevron, no pointer, no fake
                // destination — the backing service does not exist yet.
                <div
                  data-testid="profile-settings-row"
                  data-k={s.k}
                  data-wired="false"
                  style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '14px 15px', minHeight: 44, borderBottom: '1px solid var(--hairline-06)', color: 'var(--text-secondary)' }}
                >
                  <span aria-hidden style={{ flex: 'none', display: 'inline-flex' }}><s.Icon size={16} /></span>
                  <span style={{ flex: 1, fontSize: 13.5, fontWeight: 600 }}>{s.label}</span>
                  {s.meta && <span style={{ fontSize: 12, color: 'var(--text-faint)' }}>{s.meta}</span>}
                </div>
              )}
              {s.k === 'password' && passwordOpen && (
                <PasswordPanel email={email} username={accountUsername} t={t} onClose={() => setPasswordOpen(false)} />
              )}
              {s.k === 'delete' && deleteOpen && (
                <DeleteAccountPanel t={t} onClose={() => setDeleteOpen(false)} />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
